import Model, { attr } from '@warp-drive/legacy/model';
import type { Type } from '@warp-drive/core/types/symbols';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import type Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';

// Proto: provider_sif_au.Zone.
export default class Zone extends Model {
  declare [Type]: 'sif/au/zone';

  // createTime is when the resource was created in seconds from the epoch.
  @attr('number')
  declare createTime: number | undefined;

  // updateTime is the last time the resource was modified in seconds from the epoch.
  @attr('number')
  declare updateTime: number | undefined;

  // jurisdiction is the Jurisdiction for which this Zone has responsibility.
  @attr('number', { defaultValue: 0 })
  declare jurisdiction: EnumValue<typeof Jurisdiction> | 0;

  // baseUrl is the base URL for all requests to this Zone.
  @attr('string', { defaultValue: '' })
  declare baseUrl: string;

  // applicationKey is the Application Key used to authenticate to this Zone.
  @attr('string', { defaultValue: '' })
  declare applicationKey: string;

  // password is the Password used to authenticate to this Zone.
  // AES encrypted. not exposed via the API.
  @attr('string', { defaultValue: '' })
  declare password: string;

  // passwordSalt contains the salt used to encrypt the password.
  // Not used/needed in the front end.
  // sessionToken is the Session Token used to authenticate to this Zone.
  @attr('string', { defaultValue: '' })
  declare sessionToken: string;

  // sessionTokenSalt contains the salt used to encrypt the Session Token.
  // Not used/needed in the front end.
  // authenticationMethod denotes which authentication method should be used
  // when communicating with this Zone.
  @attr('number', { defaultValue: 0 })
  declare authenticationMethod: number;

  // zoneIdPattern contains a pattern for appending a Zone ID to requests.
  @attr('string', { defaultValue: '' })
  declare zoneIdPattern: string;

  // encoding denotes the encoding method which this Zone uses.
  @attr('number', { defaultValue: 0 })
  declare encoding: number;

  // oauthScopes contains a comma-separated list of scopes to request when
  // using oauth2 client credentials authentication.
  @attr('string', { defaultValue: '' })
  declare oauthScopes: string;

  // oauthTokenUrl contains the oauth2 token endpoint URL, for use with
  // oauth2 client credentials authentication.
  @attr('string', { defaultValue: '' })
  declare oauthTokenUrl: string;

  // matrixParamLocation specifies where matrix parameters should be added to
  // HTTP requests.
  @attr('number', { defaultValue: 0 })
  declare matrixParamLocation: number;

  // name is the human-friendly name for this Zone.
  @attr('string', { defaultValue: '' })
  declare name: string;
}
